import { createContext, PropsWithChildren, useCallback, useContext, useEffect } from "react";
import { api } from "../../contracts";
import { useAuth } from "../auth";
import { useChapter } from "../chapter";
import { useCourse } from "../course";
import { useMe } from "../me";
import { useVideoPlayer } from "../videoPlayer";

type LessonContextData = {
    watched: (classeId: number, lessonId: number) => void
}


const DEFAULT = {
    watched: (classeId: number, lessonId: number) => { }
}


const LessonContext = createContext<LessonContextData>(DEFAULT);

export const LessonProvider = ({ children }: PropsWithChildren<any>) => {
    const { user, updateUser } = useAuth();
    const { getMe } = useMe();
    const { type } = useCourse();
    const { chapters } = useChapter();
    const { stateChange, setConfig } = useVideoPlayer();

    const watched = useCallback(async (classeId: number, lessonId: number) => {
        if (!user) {
            throw new Error('No student is authenticated!!');
        }
        const response = await api.lesson.watched(type, classeId, lessonId);
        const userMe = await getMe();

        updateUser(userMe);

        return response;
    }, [getMe, type, updateUser, user]);

    useEffect(() => {
        if (stateChange) {
            const { event, current } = stateChange;
            if (event.data === 3) {
                const chapterId = chapters.find(l => l.lessons.some(l => l.url === current.url))?.id;
                const lessonId = chapters.map(l => l.lessons).reduce((a, b) => [...a, ...b]).find(l => l.url === current.url)?.id;
                if (chapterId && lessonId) {
                    watched(chapterId, lessonId);
                }
            }
        }
    }, [chapters, stateChange, watched]);

    useEffect(() => {
        setConfig({
            labelNext: 'Próxima Aula',
            labelPrevius: 'Aula Anterior'
        });
    }, [setConfig])

    return (
        <LessonContext.Provider
            value={{
                watched
            }}>
            {children}
        </LessonContext.Provider>
    );
};

export function useLesson(): LessonContextData {
    const context = useContext(LessonContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}